import { gql } from '@/shared/graphql/generated/explorer';

export const getMarketData = gql(/* GraphQL */ `
  query GetMarketData($asset: ChainflipAsset!, $numberOfDays: Int!) {
    marketData(asset: $asset, numberOfDays: $numberOfDays) {
      candlesticks {
        ... on CandlestickData {
          time
          open
          low
          high
          close
        }
        ... on WhitespaceData {
          time
        }
      }
      volumes {
        ... on HistogramData {
          value
          time
        }
        ... on WhitespaceData {
          time
        }
      }
      prices {
        time
        value
      }
      globalVolume
    }
  }
`);

export const getChainTrackingQuery = gql(/* GraphQL */ `
  query GetChainTrackingQuery {
    allChainTrackings {
      nodes {
        chain
        height
      }
    }
  }
`);

export const getLatestEpochsQuery = gql(/* GraphQL */ `
  query GetLatestEpochs($limit: Int!) {
    latestEpoch: allEpoches(first: $limit, orderBy: ID_DESC) {
      nodes {
        ...EpochWithRotation
      }
    }
  }
`);

export const getLatestBlocksQuery = gql(/* GraphQL */ `
  query GetLatestBlocks($limit: Int! = 10) {
    latestBlocks: allBlocks(first: $limit, orderBy: ID_DESC) {
      nodes {
        ...BlockListBlock
      }
    }
  }
`);

export const getValidatorsTotalRewardsQuery = gql(/* GraphQL */ `
  query GetValidatorsTotalRewards($validators: [String!]!) {
    accounts: allAccounts(condition: { role: VALIDATOR }, filter: { idSs58: { in: $validators } }) {
      nodes {
        idSs58
        validator: validatorByAccountId {
          authority: authorityMembershipsByValidatorId {
            aggregates {
              sum {
                reward
              }
            }
          }
          cfeVersionId
        }
      }
    }
  }
`);

export const getValidatorsAggregatedStatsQuery = gql(/* GraphQL */ `
  query GetValidatorsAggregatedStats($validators: [String!]!) {
    totalRewards: allAuthorityMemberships(
      filter: { validatorByValidatorId: { accountByAccountId: { idSs58: { in: $validators } } } }
    ) {
      aggregates {
        sum {
          reward
          rewardValueUsd
        }
      }
    }
    totalSlashed: allAccountFundingEvents(
      filter: { accountByAccountId: { idSs58: { in: $validators } }, type: { equalTo: SLASHED } }
    ) {
      aggregates {
        sum {
          amount
        }
      }
    }
  }
`);

export const getAuthorshipData = gql(/* GraphQL */ `
  query AuthorshipData($operatorId: String!, $validatorIds: [String!]!, $startTime: Datetime!) {
    lastBlock: allBlocks(orderBy: ID_DESC, first: 1) {
      nodes {
        id
      }
    }
    operatorEpochs: allOperatorEpoches(
      filter: { operatorByOperatorId: { accountByAccountId: { idSs58: { equalTo: $operatorId } } } }
      orderBy: EPOCH_ID_DESC
      first: 2
    ) {
      nodes {
        validators: operatorEpochValidatorsByOperatorEpochId {
          totalCount
        }
        epoch: epochByEpochId {
          startBlockId
          endBlockId
          memberships: authorityMembershipsByEpochId {
            totalCount
          }
        }
      }
    }
    authorshipPenalties: allPenalties(
      filter: {
        validatorByValidatorId: { accountByAccountId: { idSs58: { in: $validatorIds } } }
        blockByBlockId: { timestamp: { greaterThanOrEqualTo: $startTime } }
        reason: { equalTo: "MissedAuthorshipSlot" }
      }
    ) {
      totalCount
    }
  }
`);

export const getOperatorEpochsQuery = gql(/* GraphQL */ `
  query GetOperatorEpochs($idSs58: String!, $first: Int, $offset: Int) {
    allOperatorEpoches(
      first: $first
      offset: $offset
      orderBy: EPOCH_ID_DESC
      filter: { operatorByOperatorId: { accountByAccountId: { idSs58: { equalTo: $idSs58 } } } }
    ) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      nodes {
        operatorRewardAmount
        operatorRewardValueUsd
        operatorLossAmount
        operatorLossValueUsd
        epoch: epochByEpochId {
          id
          bond
          startBlockId
          endBlockId
        }
        validators: operatorEpochValidatorsByOperatorEpochId {
          numberOfNodes: totalCount
          nodes {
            validatorId
          }
        }
        delegations: operatorEpochDelegationsByOperatorEpochId {
          aggregates {
            sum {
              rewards
              rewardsValueUsd
              losses
              lossesValueUsd
            }
          }
        }
      }
    }
  }
`);

export const getValidatorsAuthorityMembershipByEpochIds = gql(/* GraphQL */ `
  query GetValidatorAuthorityMemberships($epochIds: [Int!]!, $ids: [Int!]!) {
    allAuthorityMemberships(
      filter: {
        epochByEpochId: { id: { in: $epochIds } }
        validatorByValidatorId: { id: { in: $ids } }
      }
      orderBy: EPOCH_ID_DESC
    ) {
      nodes {
        totalRewards: reward
        validator: validatorByValidatorId {
          id
          accountByAccountId {
            idSs58
          }
        }
        epoch: epochByEpochId {
          id
          slashedEvents: accountFundingEventsByEpochId(
            condition: { type: SLASHED }
            filter: { accountByAccountId: { validatorByAccountId: { id: { in: $ids } } } }
          ) {
            agg: aggregates {
              sum {
                amount
              }
            }
          }
        }
      }
    }
  }
`);

export const getWhitelistBlacklistTimestamp = gql(/* GraphQL */ `
  query GetWhitelistBlacklistTimestamp(
    $operatorId: String!
    $delegatorIds: [String!]!
    $type: OperatorActionType
  ) {
    accounts: allAccounts(filter: { idSs58: { in: $delegatorIds } }) {
      nodes {
        idSs58
        actions: operatorActionsByAccountId(
          filter: {
            operatorByOperatorId: { accountByAccountId: { idSs58: { equalTo: $operatorId } } }
            type: { equalTo: $type }
          }
        ) {
          aggregates {
            max {
              timestamp
            }
          }
        }
      }
    }
  }
`);
